/* Native customer form: no AJAX signup, email storage, or simulated confirmation. */
(() => {
  if (customElements.get('cibocca-newsletter-popup')) return;
  const UNTIL_KEY = 'cibocca:newsletter:until';
  const SESSION_KEY = 'cibocca:newsletter:shown';
  const DAY = 86400000;

  customElements.define('cibocca-newsletter-popup', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.form || this.dataset.enabled !== 'true') return;
      if (document.querySelector('cibocca-newsletter-popup[data-enabled="true"]') !== this) return;
      if (typeof this.dialog.showModal !== 'function') return;
      this.editor = this.dataset.editor === 'true' || !!window.Shopify?.designMode;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.lastInteraction = null;
      this.readyAt = Date.now() + Number(this.dataset.delay) * 1000;
      this.result = this.querySelector('[data-native-result]').dataset.nativeResult;
      const ownResponse = window.location.hash === `#${this.form.id}` && this.result !== 'idle';

      this.querySelector('[data-newsletter-close]').addEventListener('click', () => this.dismiss(), { signal });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')]
          .filter((element) => element.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) return;
        if (!focusable.includes(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }, { signal });
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.dismiss(); }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dismiss();
      }, { signal });
      this.form.addEventListener('submit', (event) => {
        if (this.editor) {
          event.preventDefault();
          this.showError(this.dataset.editorSubmit);
        }
        // Leave the normal POST, Shopify CAPTCHA and reload entirely to Shopify.
      }, { signal });
      this.form.addEventListener('invalid', (event) => {
        const field = event.target;
        const message = field.type === 'checkbox' ? this.dataset.requiredConsent :
          field.validity.valueMissing ? this.dataset.requiredEmail : this.dataset.invalidEmail;
        field.setCustomValidity(message);
        field.setAttribute('aria-invalid', 'true');
        this.showError(message);
      }, { signal, capture: true });
      this.form.addEventListener('input', (event) => {
        event.target.setCustomValidity?.('');
        event.target.removeAttribute('aria-invalid');
        const error = this.querySelector('[data-newsletter-error]');
        if (error) { error.hidden = true; error.textContent = ''; }
      }, { signal });
      const recordInteraction = () => { this.lastInteraction = Date.now(); };
      ['pointerdown', 'keydown', 'input', 'submit'].forEach((name) => {
        document.addEventListener(name, recordInteraction, { signal, capture: true });
      });
      window.addEventListener('pagehide', () => {
        clearTimeout(this.timer);
        this.close(false); // Do not restore an open popup from the back/forward cache.
      }, { signal });
      if (this.editor) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open('editor');
        }, { signal });
        document.addEventListener('shopify:section:load', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open('editor');
        }, { signal });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.close(false);
        }, { signal });
        return;
      }
      if (/\/(?:checkouts?|challenge|account|cart)(?:\/|$)/.test(window.location.pathname)) return;
      if (ownResponse) {
        if (this.result === 'success') this.remember(Number(this.dataset.successDays));
        this.open('response');
        return;
      }
      // A result for another newsletter/customer form must never open this popup.
      if (this.result !== 'idle' || this.dataset.eligible !== 'true' || this.dataset.subscribed === 'true') return;
      this.timer = setTimeout(() => this.attempt(), Number(this.dataset.delay) * 1000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.close(false);
    }

    suppressed() {
      try {
        return sessionStorage.getItem(SESSION_KEY) === '1' || Number(localStorage.getItem(UNTIL_KEY)) > Date.now();
      } catch (_) {
        // Without preference storage, avoid an intrusive popup on every navigation.
        return true;
      }
    }

    blockingOverlay() {
      return document.body.classList.contains('overflow-hidden') ||
        !!document.querySelector('cart-drawer.active, cart-notification.active, modal-dialog[open], menu-drawer details[open], product-form .loading') ||
        [...document.querySelectorAll('dialog[open]')].some((dialog) => dialog !== this.dialog);
    }

    busy() {
      const active = document.activeElement;
      return document.hidden || this.blockingOverlay() ||
        !!active?.matches('input, textarea, select, [contenteditable="true"]') ||
        !!active?.closest('product-form, product-info, .shopify-payment-button') ||
        (this.lastInteraction !== null && Date.now() - this.lastInteraction < 5000);
    }

    attempt() {
      if (!this.isConnected || this.dialog.open || this.suppressed()) return;
      if (Date.now() < this.readyAt || this.busy()) {
        this.timer = setTimeout(() => this.attempt(), 1000);
        return;
      }
      this.open('auto');
    }

    open(reason) {
      if (this.dialog.open || !this.isConnected) return;
      if (reason === 'auto') {
        if (this.suppressed() || this.busy()) return;
        try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) { return; }
      }
      this.opener = document.activeElement;
      this.dialog.showModal();
      document.body.classList.add('cibocca-newsletter-open');
      const focus = this.result === 'error' ? this.form.querySelector('[aria-invalid="true"]') :
        this.result === 'success' ? this.querySelector('.cibocca-newsletter__confirmation') : null;
      (focus || this.querySelector('[data-newsletter-close]')).focus({ preventScroll: true });
      // An asynchronously opened cart/other dialog takes priority over this popup.
      this.observer = new MutationObserver(() => {
        if (this.dialog.open && this.blockingOverlay()) this.close(false);
      });
      this.observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class', 'open'] });
    }

    close(restoreFocus = true) {
      this.observer?.disconnect();
      if (!this.dialog?.open) return;
      this.dialog.close();
      document.body.classList.remove('cibocca-newsletter-open');
      if (restoreFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    remember(days) {
      if (this.editor) return;
      try {
        localStorage.setItem(UNTIL_KEY, String(Date.now() + days * DAY));
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch (_) { /* Submission feedback and close controls still work. */ }
    }

    dismiss() {
      // Closing a server-confirmed receipt must not shorten its longer suppression.
      if (this.result !== 'success') this.remember(Number(this.dataset.dismissDays));
      this.close();
    }

    showError(message) {
      const error = this.querySelector('[data-newsletter-error]');
      if (error) { error.hidden = false; error.textContent = message; }
    }
  });
})();
